import { TableV2 } from 'components/Table/TableV2';
import {
  useSearchCandidatesBatchesQuery,
  useUpdateCandidateBatchMutation
} from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableCandidateBatchSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { PaginationChangeRowsPerPage } from 'react-data-table-component/dist/DataTable/types';
import { TrackingEventName } from 'services/tracking/constants';
import { useTracking } from 'services/tracking/hooks';
import { getAnalysesColumns } from '../utils/getAnalysesColumns';
import { useAnalysesFilters } from '../hooks/useAnalysesFilters';
import { useSearchablePagination } from 'common/hooks/useSearchablePagination';
import { getAnalysisFilterLabel } from '../utils/getAnalysisFilterLabel';
import { STATUS_FILTER_ID } from '../constants/filters';
import { useTableSort } from 'common/hooks/useTableSort';
import { SearchCandidateBatchesItem } from '../types/SearchCandidateBatchesItem';
import { BottomDrawerComponentType } from 'core/stores/bottomDrawer/types';
import { useBottomDrawer } from 'core/stores/bottomDrawer/hooks';
import { useState } from 'react';
import { wait } from 'utils/platform';
import { ConfirmationModalType } from 'services/modals/constants';
import {
  CandidateBatchGroup,
  CandidateBatchStatus
} from 'generated-graphql/graphql';
import { useConfirmationModal } from 'services/modals/hooks';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { DrawerComponentType } from 'services/drawer/types';
import { useDrawer } from 'services/drawer/hooks';
import { updateCandidateBatchGroupLocally } from '../utils/updateCandidateBatchGroupLocally';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import {
  externalIdFields,
  externalIdSchema,
  externalIdSchemaOptional
} from 'common/form/validationSchema/externalId';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';

type Props = {
  isAllCampaigns: boolean;
  limit: number;
  searchText: string;
  onChangeRowsPerPage: PaginationChangeRowsPerPage;
};

export const AnalysesTab: React.FC<Props> = ({
  isAllCampaigns,
  searchText,
  limit,
  onChangeRowsPerPage
}) => {
  const organizationConfig = useOrganizationConfig();
  const { track } = useTracking();
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const { from, currentPage, handleNext, handlePrevious } =
    useSearchablePagination({ limit });
  const [updateCandidateBatch] = useUpdateCandidateBatchMutation();
  const { showModal } = useConfirmationModal();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const { openDrawer } = useDrawer();
  const { openModal } = useModalV2();
  const isExternalIdRequiredFlag = useFlag(Flags.EXTERNAL_ID_IS_MANDATORY);
  const isEditExternalIdEnabledFlag = useFlag(Flags.EDIT_EXTERNAL_ID);
  const navigate = useNavigate();

  const { sort, handleSort } = useTableSort<
    SearchableCandidateBatchSortableFields,
    SearchCandidateBatchesItem
  >({
    field: SearchableCandidateBatchSortableFields.UpdatedAt,
    direction: SearchableSortDirection.Desc
  });

  const {
    closeBottomDrawer,
    openBottomDrawer,
    isOpen,
    updateBottomDrawerComponentProps
  } = useBottomDrawer();

  const { filter, isFiltering, filters, setFilters } = useAnalysesFilters({
    isAllCampaigns,
    searchText
  });

  const { loading, data, refetch, updateQuery } =
    useSearchCandidatesBatchesQuery({
      variables: {
        filter,
        limit,
        sort,
        from
      }
    });

  const items = data?.searchCandidateBatches?.items?.filter((item) => !!item);
  const total = data?.searchCandidateBatches?.total;

  const refetchWithDelay = async () => {
    // wait 2 seconds and then refetch to allow searchable DB to update
    await wait(2000);
    await refetch();
  };

  const onClearSelectedCandidateBatches = async () => {
    closeBottomDrawer();
    setClearSelectedRows(true);
    setTimeout(() => setClearSelectedRows(false), 0); // Reset to false to allow subsequent clears
  };

  const onApplyRulesetSuccess = (
    candidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>,
    candidateBatchIds: string[]
  ) => {
    updateQuery((prevData) =>
      updateCandidateBatchGroupLocally(
        prevData,
        candidateBatchIds,
        candidateBatchGroup
      )
    );
  };

  const handleApplyRuleset = (
    candidateBatchIds: string[],
    initialCandidateBatchGroup?: Pick<CandidateBatchGroup, 'id' | 'name'>
  ) => {
    openDrawer({
      drawerContentType: DrawerComponentType.RulesetDrawer,
      drawerComponentProps: {
        candidateBatchIds,
        initialCandidateBatchGroup: initialCandidateBatchGroup && {
          id: initialCandidateBatchGroup.id,
          name: initialCandidateBatchGroup.name
        },
        onSuccess: (
          candidateBatchGroup: Pick<CandidateBatchGroup, 'id' | 'name'>
        ) => onApplyRulesetSuccess(candidateBatchGroup, candidateBatchIds)
      }
    });
  };

  const onSelectRows = ({
    rowsSelected
  }: {
    rowsSelected: SearchCandidateBatchesItem[];
  }) => {
    const candidateBatchIdsSelected = rowsSelected.map((item) => item.id);
    const candidateBatchesSelected = rowsSelected;

    if (rowsSelected.length > 0) {
      if (isOpen) {
        updateBottomDrawerComponentProps({
          candidateBatchIdsSelected,
          candidateBatchesSelected
        });
      } else {
        openBottomDrawer({
          drawerContentType: BottomDrawerComponentType.AnalysesListActions,
          drawerComponentProps: {
            candidateBatchIdsSelected,
            candidateBatchesSelected,
            onSuccess: refetchWithDelay,
            onApplyRulesetSuccess
          },
          onClose: onClearSelectedCandidateBatches
        });
      }
    } else {
      closeBottomDrawer();
    }
  };

  const handleArchive = async (candidateBatchId: string) => {
    try {
      showLoadingOverlay();
      await updateCandidateBatch({
        variables: {
          input: { id: candidateBatchId, status: CandidateBatchStatus.Archived }
        }
      });

      await refetchWithDelay();
      showToast({
        title: 'Analysis archived',
        type: ToastType.Success
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  const handleArchiveConfirmation = (candidateBatchId: string) => {
    showModal({
      title: `Archive this analysis`,
      message:
        'Once archived, analyses will be stopped for all applicants. Archived analyses cannot be re-activated in the future.',
      modalType: ConfirmationModalType.DESTRUCTIVE,
      onDestructiveConfirm: () => handleArchive(candidateBatchId),
      destructiveText: 'Archive',
      cancelText: 'Keep'
    });
  };

  const handleEditExternalIdClick = (
    candidateBatchId: string,
    externalId?: string | null
  ) => {
    openModal({
      title: 'Edit external ID',
      form: {
        schema: isExternalIdRequiredFlag
          ? externalIdSchema
          : externalIdSchemaOptional,
        fields: externalIdFields,
        defaultValues: {
          externalId: externalId ?? ''
        }
      },
      onPrimaryAction: async (values) => {
        await updateCandidateBatch({
          variables: {
            input: {
              id: candidateBatchId,
              externalId: values.externalId || undefined
            }
          }
        });

        await refetchWithDelay();

        showToast({
          title: 'External ID updated',
          type: ToastType.Success
        });
      }
    });
  };

  const handleStartAnalysis = (candidateBatchId: string) => {
    navigate(`${Paths.ANALYSE_CREATE}/${candidateBatchId}`);
  };

  const onFilterSelect = ({
    value,
    filterId
  }: {
    value: string | boolean;
    filterId?: string;
  }) => {
    if (filterId === STATUS_FILTER_ID) {
      setFilters((prevFilters) => [
        ...prevFilters.filter((filter) => filter.filterId !== filterId),
        {
          filterId: STATUS_FILTER_ID,
          value: value as CandidateBatchStatus,
          label: getAnalysisFilterLabel(value as CandidateBatchStatus)
        }
      ]);
    }
  };

  const columns = getAnalysesColumns({
    sourcingEnabled: organizationConfig?.sourcingEnabled ?? false,
    internalSourcingEnabled:
      organizationConfig?.internalSourcingEnabled ?? false,
    handleArchiveConfirmation,
    handleApplyRuleset,
    handleEditExternalIdClick,
    track,
    isEditExternalIdEnabled: isEditExternalIdEnabledFlag,
    handleStartAnalysis
  });

  return (
    <TableV2
      columns={columns}
      data={items ?? []}
      pagination
      paginationServer
      paginationTotalRows={total ?? 0}
      isLoading={loading}
      sortField={sort.field}
      sortServer
      onSort={handleSort}
      isFiltering={isFiltering}
      serverPage={currentPage}
      paginationResetDefaultPage={false}
      handleNext={() => {
        track(TrackingEventName.AnalyseListNextPage);
        handleNext();
      }}
      handlePrevious={() => {
        handlePrevious();
      }}
      onChangeRowsPerPage={onChangeRowsPerPage}
      selectableRows
      clearSelectedRows={clearSelectedRows}
      onSelectedRowsChange={(params) => {
        onSelectRows({
          rowsSelected: params.selectedRows
        });
      }}
      filters={filters}
      onFilterSelect={onFilterSelect}
      onRemoveFilters={(filterId) =>
        setFilters((prevFilters) =>
          prevFilters.filter((filter) => filter.filterId !== filterId)
        )
      }
      onRemoveAllFilters={() => setFilters([])}
    />
  );
};
